import {  reactive, ref } from "vue";
import { defineStore } from "pinia";
import type { LoadingStatus } from "./loading_status";
import { ElMessage } from "element-plus";
import type { AttachedStoragePolicy, EncryptionScheme } from "./storage_policy";
import S3ApiService from "@/core/services/S3ApiService";
import { parseUtcDate } from "@/core/helpers/utils";

// SkyFlok S3 Dev user profile
export interface Bucket {
  id: number
  name: string
  namespace_id: number
  objects_num?: number
  versions_num?: number
  total_size?: number
  created?: Date
  download_strategy?: string
}

export interface S3Object {
  key: string
  name: string
  size?: number
  eTag?: string
  lastModified: Date
  loading: boolean
  percent?: number
  versions: Array<S3ObjectVersion | S3ObjectVersionDeleteMarker>
}

export interface S3ObjectVersionDeleteMarker {
  key: string
  versionId: string
  isLatest: boolean
  lastModified: Date
}
export interface S3ObjectVersion extends S3ObjectVersionDeleteMarker {
  size: number
  eTag?: string
  degradedWriteId?: number
}

export interface BucketWithPolicy extends Bucket {
  policy: AttachedStoragePolicy | null
}

export interface ObjectsViewOptions {
  show_folders: boolean
  show_delete_markers: boolean
}

export interface DownloadLogViewOptions {
  hide_events_of_deleted_objects: boolean
}

// Packet in a SkyFlok File Version
export interface FileVersionGenerationPacket {
  name: string
  bucket_id?: number
  edge_device_id?: number
}

// SkyFlok File Version Generation
export interface FileVersionGeneration {
  idx: number
  size: number
  redundancy_scheme: 'rlnc' | 'replication'
  symbols?: number
  symbol_size?: number
  packets: FileVersionGenerationPacket[]
  encryption?: EncryptionScheme
}

// SkyFlok File Version
export interface FileVersionDetails {
  id: number
  file_id: number
  size: number /* -1 for delete marker */
  generations: FileVersionGeneration[] | null /* NULL=Delete marker */
  hash: string | null
  timestamp: Date
}

// Selected object and its bucket, for the Object Details modal
export const selected_object = ref<S3Object | null>(null)
export const selected_object_bucket = ref<Bucket | null>(null)

// Selected bucket for the SP change modal
export const selected_bucket = ref<Bucket|null>(null)

export const BUCKET_NAME_MIN_LENGTH = 3
export const BUCKET_NAME_MAX_LENGTH = 63

// Listed in this order in the new bucket wizard
export const DOWNLOAD_STRATEGIES = [
  {
    key: 'fastest',
  },
  {
    key: 'closest',
    default: true,
  },
  {
    key: 'cheapest',
    no_bucket_config: true
  },
  {
    key: 'default',
    no_bucket_config: true // Cannot be used as a bucket config option, only for download metrics
  }
]

export const useBucketStore = defineStore("bucket", () => {
  const buckets_loaded = ref(false)
  const buckets = reactive<Bucket[]>([])
  const buckets_loading = reactive<LoadingStatus>({loading: false})

  async function load_buckets(with_objects_count: boolean | null = true): Promise<null> {
    if(buckets_loading.loading) return null

    buckets_loaded.value = false
    buckets_loading.loading = true
    return S3ApiService.list_buckets(with_objects_count).then(async ({data}) => {

      data.forEach(bucket => {
        bucket.created = parseUtcDate(bucket.created)
      });
      buckets.splice(0)
      buckets.push(...data)
      buckets_loaded.value = true
    }).catch(({response}) => {
      console.error(`Error loading buckets:`, response)
      ElMessage({
        type: 'error',
        message: 'Error loading Buckets'
      })
    }).finally(() => {
      buckets_loading.loading = false
    })
  }

  const change_download_strategy_loading = reactive<LoadingStatus>({loading: false})
  async function change_download_strategy(bucket_name: string, new_strategy: string): Promise<boolean>{
    // Change the bucket's download strategy, return whether the change was successful
    if(change_download_strategy_loading.loading) return false

    change_download_strategy_loading.loading = true
    return S3ApiService.change_download_strategy(bucket_name, new_strategy).then(({data}) => {
      return true
    }).catch(({response}) => {
      console.error("Error changing bucket download strategy to "+ new_strategy, response)
      ElMessage({
        type: 'error',
        message: 'Error changing download strategy'
      })
    }).finally(() => {
      change_download_strategy_loading.loading = false
    })
  }

  const apikey_access_name_resolver = (access: string) => {
    switch(access){
      case 'read': return "Read Only"
      case "full": return "Full Access"
      default: return ""
    }
  }

  const bucket_name_resolver = (name) =>
    name === "__new__" ? "New Buckets" : name

  return {
    buckets,
    buckets_loading,
    buckets_loaded,
    load_buckets,
    access_name: apikey_access_name_resolver,
    bucket_name: bucket_name_resolver,

    change_download_strategy_loading,
    change_download_strategy
  };
});
